import { apiFetch } from '@/lib/api'

// ---- Super Powers --------------------------------------------------------

export interface Flag {
  key: string
  label: string
  description: string
  group: string
  destructive: boolean
  value: boolean
  type: string
  last_changed_at: string | null
  last_changed_by: string | null
}

export interface FlagsResponse {
  groups: Record<string, Flag[]>
  flags: Flag[]
  total: number
}

export interface FlagChange {
  id: number
  setting: string
  before: Record<string, unknown>
  after: Record<string, unknown>
  actor: string | null
  actor_type: string
  note: string
  created_at: string
}

export interface ScheduledChange {
  id: number
  setting: string
  target_value: unknown
  run_at: string
  status: 'pending' | 'done' | 'cancelled' | 'failed'
  actor: string | null
  note: string
  created_at: string
  executed_at: string | null
  error: string
}

export const fetchFlags = () =>
  apiFetch<FlagsResponse>('/api/admin/super-powers/flags/')
export const patchFlag = (key: string, value: boolean, note?: string) =>
  apiFetch(`/api/admin/super-powers/flags/${encodeURIComponent(key)}/`, {
    method: 'PATCH',
    body: { value, note: note ?? '' }
  })
export const bulkPatchFlags = (keys: string[], value: boolean) =>
  apiFetch('/api/admin/super-powers/flags/bulk/', {
    method: 'POST',
    body: { keys, value }
  })
export const fetchFlagHistory = (key: string) =>
  apiFetch<{ results: FlagChange[] }>(
    `/api/admin/super-powers/flags/${encodeURIComponent(key)}/history/`
  )
export const fetchChangesToday = () =>
  apiFetch<{ results: FlagChange[] }>(
    '/api/admin/super-powers/flags/changes-today/'
  )
export const fetchScheduledFlags = () =>
  apiFetch<{ results: ScheduledChange[] }>(
    '/api/admin/super-powers/flags/scheduled/'
  )
export const scheduleFlag = (
  key: string,
  target_value: unknown,
  run_at: string,
  note?: string
) =>
  apiFetch<ScheduledChange>(
    `/api/admin/super-powers/flags/${encodeURIComponent(key)}/schedule/`,
    {
      method: 'POST',
      body: { target_value, run_at, note: note ?? '' }
    }
  )
export const cancelScheduledFlag = (id: number) =>
  apiFetch(`/api/admin/super-powers/flags/scheduled/${id}/`, {
    method: 'DELETE'
  })

// ---- RBAC ----------------------------------------------------------------

export interface ScopeCatalogItem {
  scope: string
  label: string
  resource: string
}

export const fetchScopeCatalog = () =>
  apiFetch<{ groups: Record<string, ScopeCatalogItem[]>; verbs: string[] }>(
    '/api/admin/rbac/scopes-catalog/'
  )

export interface StaffMember {
  id: number
  username: string
  email: string
  is_superuser: boolean
  scope_count: number
}

export const fetchStaffList = (q?: string) => {
  const url = q
    ? `/api/admin/rbac/staff/?q=${encodeURIComponent(q)}`
    : '/api/admin/rbac/staff/'
  return apiFetch<{ results: StaffMember[] }>(url)
}

export interface UserScopes {
  user_id: number
  username: string
  is_superuser: boolean
  is_staff: boolean
  scopes: string[]
}

export const fetchUserScopes = (userId: number) =>
  apiFetch<UserScopes>(`/api/admin/rbac/users/${userId}/scopes/`)

export const setUserScopes = (userId: number, scopes: string[]) =>
  apiFetch(`/api/admin/rbac/users/${userId}/scopes/`, {
    method: 'PATCH',
    body: { scopes }
  })

// ---- CEO Autonomy --------------------------------------------------------

export type Autonomy = 'cautious' | 'normal' | 'eager'

export const setCeoAutonomy = (conversationId: string, autonomy: Autonomy) =>
  apiFetch(`/api/admin/ceo/conversations/${conversationId}/autonomy/`, {
    method: 'PATCH',
    body: { autonomy }
  })
